import {useQueryClient} from '@tanstack/react-query';

import {
  useMemberCandidatesQuery,
  useMemberEventsQuery,
  useTeamMemberActions
} from '@/features/trust-accounts/members';
import {useTrustAccountPermissions} from '@/features/trust-accounts/providers';
import {invalidateTrustAccountTeam} from '@/features/trust-accounts/utils/trust-account-invalidations.utils';
import {useCurrentUserIdentity} from '@/hooks';

import {useShareGuestActions} from './useShareGuestActions';
import {useShareLink} from './useShareLink';
import {
  useTrustAccountGuestEventsQuery,
  useTrustAccountGuestsQuery
} from './useTrustAccountGuestsQueries';

import type {ShareInviteFormParams} from './useShareInviteForm';
import type {ShareView} from '../utils';
import type {
  TrustAccountGuestDTO,
  TrustAccountGuestEventDTO,
  TrustAccountMemberCandidateDTO,
  TrustAccountMemberEventDTO,
  TrustAccountMembersDTO
} from '@sollapay/types';

const NO_MEMBER_EVENTS: TrustAccountMemberEventDTO[] = [];
const NO_GUEST_EVENTS: TrustAccountGuestEventDTO[] = [];
const NO_CANDIDATES: TrustAccountMemberCandidateDTO[] = [];

/**
 * Everything the open share panel shows and does. The logs load only on the log view, and the firm
 * users only for the trustee, who alone may add people.
 */
export function useSharePanelData(params: {
  trustAccountId: string;
  roster: TrustAccountMembersDTO;
  guests: TrustAccountGuestDTO[];
  view: ShareView;
}) {
  const {trustAccountId, roster, guests, view} = params;
  const {canManageMembers} = useTrustAccountPermissions();
  const log = useShareLog(trustAccountId, view === 'log');
  const {isError: isGuestsError, refetch: refetchGuests} =
    useTrustAccountGuestsQuery(trustAccountId);
  const candidates = useShareCandidates(trustAccountId, canManageMembers);
  const memberActions = useShareMemberActions(trustAccountId);
  const {invite, remove, resend} = useShareGuestActions(trustAccountId, guests);
  const {copyLink, linkCopied} = useShareLink(trustAccountId);
  const {userId: currentUserId} = useCurrentUserIdentity();

  return {
    ...log,
    isGuestsError,
    onRetryGuests: () => void refetchGuests(),
    ...candidates,
    ...memberActions,
    readOnly: !canManageMembers,
    currentUserId,
    roster,
    guests,
    onInviteGuests: invite,
    onRemoveGuest: remove,
    onResendGuest: resend,
    onCopyLink: () => void copyLink(),
    linkCopied
  };
}

function useShareLog(trustAccountId: string, enabled: boolean) {
  const {
    data: memberEvents,
    isLoading: isMemberEventsLoading,
    isError: isMemberEventsError,
    refetch: refetchMemberEvents
  } = useMemberEventsQuery(trustAccountId, {enabled});
  const {
    data: guestEvents,
    isLoading: isGuestEventsLoading,
    isError: isGuestEventsError,
    refetch: refetchGuestEvents
  } = useTrustAccountGuestEventsQuery(trustAccountId, {enabled});

  return {
    memberEvents: memberEvents ?? NO_MEMBER_EVENTS,
    guestEvents: guestEvents ?? NO_GUEST_EVENTS,
    isLogLoading: isMemberEventsLoading || isGuestEventsLoading,
    isLogError: isMemberEventsError || isGuestEventsError,
    onRetryLog: () => {
      if (isMemberEventsError) void refetchMemberEvents();
      if (isGuestEventsError) void refetchGuestEvents();
    }
  };
}

function useShareCandidates(trustAccountId: string, enabled: boolean) {
  const {data, status, refetch} = useMemberCandidatesQuery(trustAccountId, {enabled});

  return {
    candidates: data ?? NO_CANDIDATES,
    candidatesStatus: status,
    onRetryCandidates: () => void refetch()
  };
}

/** Each add skips its own team refetch; the send refreshes the team once after its last add. */
function useShareMemberActions(trustAccountId: string) {
  const queryClient = useQueryClient();
  const {add, move, remove, error} = useTeamMemberActions(trustAccountId);

  const onAddMember: ShareInviteFormParams['onAddMember'] = (person, role) =>
    add(person, role, {deferRefresh: true});

  return {
    memberError: error,
    onAddMember,
    onMembersAdded: () => invalidateTrustAccountTeam(queryClient, trustAccountId),
    onMoveMember: move,
    onRemoveMember: remove
  };
}
